import type { HttpClient } from '@/lib/http/client.js'
import type {
  RecoveryCodesIssued,
  RequestEmailChangeInput,
  TwoFactorProofInput,
  TwoFactorSetup,
  TwoFactorStatus,
  UpdateProfileInput,
} from '@/types/account/account.type.js'

/**
 * The caller's own account on `admin/account`. No permission needed — it is theirs.
 *
 * The two-factor routes are verified against `admin-two-factor.controller.ts`: `setup`
 * stores a PENDING secret and changes nothing at login until `confirm` proves the phone
 * has it; `disable` and `recovery-codes` need the password AND a current code, because a
 * stolen session alone must not be able to weaken the account.
 */
export interface AccountService {
  updateProfile: (input: UpdateProfileInput) => Promise<{ name: string }>
  /** 202. The confirmation goes to the NEW address; nothing changes until it is used. */
  requestEmailChange: (input: RequestEmailChangeInput) => Promise<void>
  twoFactorStatus: () => Promise<TwoFactorStatus>
  /** 503 when the server has no at-rest key. */
  twoFactorSetup: () => Promise<TwoFactorSetup>
  /** 400 with no setup in progress or a wrong code. Answers the recovery codes, once. */
  twoFactorConfirm: (code: string) => Promise<RecoveryCodesIssued>
  twoFactorDisable: (input: TwoFactorProofInput) => Promise<void>
  twoFactorRecoveryCodes: (input: TwoFactorProofInput) => Promise<RecoveryCodesIssued>
}

export function createAccountService(http: HttpClient): AccountService {
  return {
    updateProfile: (input) => http.patch<{ name: string }>('admin/account/profile', input),
    requestEmailChange: async (input) => {
      await http.post('admin/account/email/request-change', input)
    },
    twoFactorStatus: () => http.get<TwoFactorStatus>('admin/account/two-factor'),
    twoFactorSetup: () => http.post<TwoFactorSetup>('admin/account/two-factor/setup'),
    twoFactorConfirm: (code) => http.post<RecoveryCodesIssued>('admin/account/two-factor/confirm', { code }),
    twoFactorDisable: async (input) => {
      await http.post('admin/account/two-factor/disable', input)
    },
    twoFactorRecoveryCodes: (input) =>
      http.post<RecoveryCodesIssued>('admin/account/two-factor/recovery-codes', input),
  }
}
